/**
 * 关键词数据 Hook
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  getWeeklyReportKeywordData,
  IKeywordItem,
  IKeywordResponse,
  IKeywordSummaryItem,
} from '@op/services';
import { DBO_REQ, DBO_V } from '@op/utils/format';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import type { TRequestBackType } from '@op/utils/requestUtil';

interface IUseKeywordDataOptions {
  /** 是否自动请求，默认 true */
  autoFetch?: boolean;
}

export interface IKeywordItemWithQoq extends IKeywordItem {
  /** 环比增长率 */
  qoq_rate: number;
  /** 格式化后的环比值 */
  qoq_formatted: number;
}

/** 处理后的数据状态 */
interface IProcessedData {
  /** 列表数据（用于表格、条形图） */
  listData: IKeywordItemWithQoq[];
  /** 折线图数据（TRequestBackType 格式） */
  trendData: TRequestBackType;
  /** 词云图数据 */
  wordCloudData: Array<{ name: string; value: number; sentiment: number }>;
  /** 波动最大的关键词 TOP3 */
  topFluctuation: IKeywordSummaryItem[];
  /** 突增关键词 TOP2 */
  newKeywords: IKeywordSummaryItem[];
  /** 关键词映射：keyword -> IKeywordItem */
  keywordMap: Record<string, IKeywordItem>;
}

interface IUseKeywordDataResult {
  /** 列表数据（用于表格、条形图） */
  listData: IKeywordItemWithQoq[];
  /** 折线图数据（TRequestBackType 格式） */
  trendData: TRequestBackType;
  /** 词云图数据 */
  wordCloudData: Array<{ name: string; value: number; sentiment: number }>;
  loading: boolean;
  empty: boolean;
  error: any;
  /** 波动最大的关键词 TOP3 */
  topFluctuation: IKeywordSummaryItem[];
  /** 突增关键词 TOP2 */
  newKeywords: IKeywordSummaryItem[];
  /** 手动刷新数据方法 */
  fetchData: (params: any) => Promise<any>;
  /** 关键词映射：keyword -> IKeywordItem，用于根据语言获取名称 */
  keywordMap: Record<string, IKeywordItem>;
}

/**
 * 将 API 返回的趋势数据转换为 TRequestBackType 格式
 * 使用 DBO_REQ.moreKeys 进行数据转换
 * @param response - API 返回的完整响应数据
 * @returns TRequestBackType 格式的图表数据
 */
const transformTrendData = (
  response: IKeywordResponse | null,
  filter: any
): TRequestBackType => {
  if (!response?.trend || response.trend.length === 0) {
    return { isEmpty: true, xAxis: [], series: [], legend: [] };
  }

  const { trend, trend_time } = response;

  // 提取所有关键词（取第一个日期的关键词列表）
  const keywordList = trend[0]?.keyword || [];

  // 构建扁平化的数据数组，每个日期一行
  // 使用 mentions 值
  const timeline = trend.map(dateItem => {
    const row: Record<string, any> = {
      date: dateItem.date,
    };
    keywordList.forEach(kw => {
      const found = dateItem.keyword.find(k => k.keyword === kw.keyword);
      row[kw.keyword] = found?.mentions || 0;
    });
    return row;
  });
  return DBO_REQ.moreKeys({
    timeline,
    timeline_of_start_time: trend_time?.start_time || '',
    timeline_of_end_time: trend_time?.end_time || '',
    filter,
    timeKey: 'date',
    needTable: true,
    addCountValue: item => {
      return { sentiment: item.avg_sentiment };
    },
  });
};

/**
 * 为列表数据添加环比计算
 * @param list - 关键词列表
 * @returns 带环比数据的列表
 */
const enrichListWithQoq = (list: IKeywordItem[]): IKeywordItemWithQoq[] => {
  return list.map(item => {
    const qoqRate = DBO_V.qoq(item.mentions, item.old_mentions, 2);
    return {
      ...item,
      qoq_rate: qoqRate,
      qoq_formatted: qoqRate,
    };
  });
};

/**
 * 转换词云图数据
 * @param list - 关键词列表
 * @returns 词云图数据格式
 */
const transformWordCloudData = (
  list: IKeywordItem[],
): Array<{ name: string; value: number; sentiment: number }> => {
  return list.map(item => ({
    name: item.keyword_cn || item.keyword,
    value: item.mentions,
    sentiment: item.avg_sentiment,
  }));
};

/**
 * 构建关键词映射
 * @param list - 关键词列表
 * @returns keyword -> IKeywordItem 的映射
 */
const buildKeywordMap = (
  list: IKeywordItem[],
): Record<string, IKeywordItem> => {
  const map: Record<string, IKeywordItem> = {};
  list.forEach(item => {
    map[item.keyword] = item;
  });
  return map;
};

/**
 * 处理 API 返回的原始数据
 * @param response - API 返回的响应数据
 * @returns 处理后的数据
 */
const processResponseData = (
  response: IKeywordResponse | null,
  filter: any
): IProcessedData => {
  const emptyData: IProcessedData = {
    listData: [],
    trendData: { isEmpty: true, xAxis: [], series: [], legend: [] },
    wordCloudData: [],
    topFluctuation: [],
    newKeywords: [],
    keywordMap: {},
  };

  if (!response) {
    return emptyData;
  }

  const rawList = response.list || [];

  return {
    // 列表数据，添加环比计算（用于横向柱状图）
    listData: enrichListWithQoq(rawList),
    // 折线图数据（使用 mentions 值）
    trendData: transformTrendData(response, filter),
    // 词云图数据（需要 mentions 和 avg_sentiment）
    wordCloudData: transformWordCloudData(rawList),
    // 波动最大的关键词 TOP3
    topFluctuation: response.summary?.top_fluctuation || [],
    // 突增关键词 TOP2
    newKeywords: response.summary?.sudden_keywords || [],
    // 关键词映射
    keywordMap: buildKeywordMap(rawList),
  };
};

export const useKeywordData = (
  filter?: any,
  options?: IUseKeywordDataOptions,
): IUseKeywordDataResult => {
  const { autoFetch = true } = options || {};
  const [processedData, setProcessedData] = useState<IProcessedData>({
    listData: [],
    trendData: { isEmpty: true, xAxis: [], series: [], legend: [] },
    wordCloudData: [],
    topFluctuation: [],
    newKeywords: [],
    keywordMap: {},
  });

  const { loading, error, wrapperCancelRequest } =
    useCancelRequestHook<IKeywordResponse>({
      clear: () =>
        setProcessedData({
          listData: [],
          trendData: { isEmpty: true, xAxis: [], series: [], legend: [] },
          wordCloudData: [],
          topFluctuation: [],
          newKeywords: [],
          keywordMap: {},
        }),
    });

  const fetchData = useCallback(
    (params: any) => {
      return wrapperCancelRequest(getWeeklyReportKeywordData, params)
        .then(res => {
          const data = processResponseData(res || null, params);
          setProcessedData(data);
          return res;
        })
        .catch(err => {
          console.error('获取关键词数据失败:', err);
        });
    },
    [], // ⚠️ 依赖数组必须为空，不要包含 wrapperCancelRequest（它是稳定引用）
  );

  // 自动请求：filter 存在且 autoFetch 为 true 时才请求
  useEffect(() => {
    if (!filter || !autoFetch) return;
    // 检查必要参数
    if (!filter?.start_time || !filter?.end_time) return;
    fetchData(filter);
  }, [filter, autoFetch, fetchData]);

  const empty = useMemo(
    () => !loading && processedData.listData.length === 0,
    [loading, processedData.listData.length],
  );

  return {
    ...processedData,
    loading,
    empty,
    error,
    fetchData,
  };
};

export default useKeywordData;
